import { useState } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { 
  LayoutDashboard, 
  Users, 
  Clock, 
  CalendarDays, 
  Coins, 
  Briefcase, 
  Star, 
  Settings,
  Menu,
  X,
  LogOut,
  Brain,
  ClipboardCheck,
  FolderOpen,
  FileBarChart,
  Network,
  User,
  Megaphone,
  Wallet,
  ListChecks,
  TrendingUp,
  BarChart3,
  FileText,
  Package,
  UserMinus,
  Receipt,
} from "lucide-react";
import { Button } from "./ui/button";

// Admin nav items with required permissions
const adminNavItems = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", module: "dashboard" },
  { to: "/analytics", icon: BarChart3, label: "Analytics", highlight: true, module: "analytics" },
  { to: "/employees", icon: Users, label: "Employees", module: "employees" },
  { to: "/attendance", icon: Clock, label: "Attendance", module: "attendance" },
  { to: "/leaves", icon: CalendarDays, label: "Leaves", module: "leaves" },
  { to: "/leave-policy", icon: FileText, label: "Leave Policy", module: "leave_policy" },
  { to: "/payroll", icon: Coins, label: "Payroll", module: "payroll" },
  { to: "/recruitment", icon: Briefcase, label: "Recruitment", module: "recruitment" },
  { to: "/onboarding", icon: ClipboardCheck, label: "Onboarding", module: "onboarding" },
  { to: "/documents", icon: FolderOpen, label: "Documents", module: "documents" },
  { to: "/org-chart", icon: Network, label: "Org Chart", module: "org_chart" },
  { to: "/performance", icon: Star, label: "Performance", module: "performance" },
  { to: "/appraisals", icon: TrendingUp, label: "Appraisals", module: "appraisals" },
  { to: "/ai-performance", icon: Brain, label: "AI Agent", module: "ai_performance" },
  { to: "/announcements", icon: Megaphone, label: "Announcements", module: "announcements" },
  { to: "/advances-loans", icon: Wallet, label: "Advances & Loans", module: "advances" },
  { to: "/assets", icon: Package, label: "Assets", module: "assets" },
  { to: "/exit-management", icon: UserMinus, label: "Exit Management", module: "exit_management" },
  { to: "/fnf-settlement", icon: Receipt, label: "F&F Settlement", module: "fnf_settlement" },
  { to: "/hr-tasks", icon: ListChecks, label: "HR Tasks", module: "hr_tasks" },
  { to: "/reports", icon: FileBarChart, label: "Reports", module: "reports" },
  { to: "/settings", icon: Settings, label: "Settings", module: "settings" },
];

// HR Assistant limited nav items
const hrAssistantNavItems = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", module: "dashboard" },
  { to: "/employees", icon: Users, label: "Employees", module: "employees" },
  { to: "/attendance", icon: Clock, label: "Attendance", module: "attendance" },
  { to: "/onboarding", icon: ClipboardCheck, label: "Onboarding", module: "onboarding" },
  { to: "/documents", icon: FolderOpen, label: "Documents", module: "documents" },
];

const employeeNavItems = [
  { to: "/my-dashboard", icon: LayoutDashboard, label: "My Dashboard" },
  { to: "/my-profile", icon: User, label: "My Profile" },
  { to: "/my-attendance", icon: Clock, label: "My Attendance" },
  { to: "/my-leaves", icon: CalendarDays, label: "My Leaves" },
  { to: "/my-payslips", icon: Coins, label: "My Payslips" },
  { to: "/my-onboarding", icon: ClipboardCheck, label: "My Onboarding" },
  { to: "/my-documents", icon: FolderOpen, label: "My Documents" },
  { to: "/my-announcements", icon: Megaphone, label: "Announcements" },
  { to: "/my-advances", icon: Wallet, label: "My Advances" },
  { to: "/my-performance", icon: Star, label: "My Performance" },
];

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { logout, user, permissions } = useAuth();

  // Determine nav items based on role
  const getNavItems = () => {
    if (user?.role === "employee") {
      return employeeNavItems;
    }
    if (user?.role === "hr_assistant" || permissions?.is_hr_assistant) {
      return hrAssistantNavItems;
    }
    return adminNavItems;
  };

  const navItems = getNavItems();

  const NavItem = ({ to, icon: Icon, label, highlight }) => (
    <NavLink
      to={to}
      onClick={() => setIsOpen(false)}
      className={({ isActive }) =>
        `sidebar-link ${isActive ? "sidebar-link-active" : ""} ${highlight ? "bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-100" : ""}`
      }
      data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <Icon className={`h-5 w-5 ${highlight ? "text-purple-600" : ""}`} />
      <span className={highlight ? "text-purple-700 font-medium" : ""}>{label}</span>
      {highlight && (
        <span className="ml-auto text-[10px] font-bold bg-gradient-to-r from-purple-500 to-indigo-600 text-white px-2 py-0.5 rounded-full">
          AI
        </span>
      )}
    </NavLink>
  );

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-xl bg-white shadow-lg"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="mobile-menu-btn"
      >
        {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
      </button>

      {/* Overlay */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/20 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-full w-64 glass-sidebar z-50 transform transition-transform duration-300 lg:translate-x-0 ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-100">
            <h1 className="text-2xl font-bold text-slate-800 font-heading">
              Tribe<span className="text-primary">HR</span>
            </h1>
            {user?.role === "employee" && (
              <p className="text-xs text-slate-400 mt-0.5">Self-Service Portal</p>
            )}
            {user?.role === "hr_assistant" && (
              <p className="text-xs text-violet-500 mt-0.5">HR Assistant</p>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <NavItem key={item.to} {...item} />
            ))}
          </nav>

          {/* User & Logout */}
          <div className="p-4 border-t border-slate-100">
            <div className="flex items-center gap-3 px-4 py-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-primary font-semibold">
                  {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-xs text-slate-500 capitalize truncate">
                  {user?.role?.replace("_", " ")}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 text-slate-500 hover:text-red-500 hover:bg-red-50"
              onClick={logout}
              data-testid="logout-btn"
            >
              <LogOut className="h-5 w-5" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
